import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PageHeader } from '@/components/composites/states'
import { UserAvatar } from '@/components/composites/user-avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { HAPPINESS, followThrough, formatMinutes } from './hours'
import { monthGrid, shiftMonth } from './calendar'
import { useDiaryMonth, type DiaryMonth } from './api'

const ALL = 'all'
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const emojiOf = (n: number | null | undefined) => HAPPINESS.find((h) => h.value === n)?.emoji ?? null
const todayKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Everyone's diary, by month. Defaults to yours; pick a colleague or look at the whole team. */
export function DiaryPage() {
  const { userId } = useIdentity()
  const [month, setMonth] = useState(() => todayKey().slice(0, 7))
  const [who, setWho] = useState<string>(userId || ALL)
  const [selected, setSelected] = useState(todayKey())
  const query = useDiaryMonth(month)
  const data = query.data

  const byDay = useMemo(() => {
    const map = new Map<string, DiaryMonth['entries']>()
    for (const e of data?.entries ?? []) map.set(e.date, [...(map.get(e.date) ?? []), e])
    return map
  }, [data])
  const names = useMemo(() => new Map((data?.people ?? []).map((p) => [p.userId, p.displayName])), [data])
  const dayEntries = (byDay.get(selected) ?? []).slice().sort((a, b) => (a.userId === userId ? -1 : b.userId === userId ? 1 : (names.get(a.userId) ?? '').localeCompare(names.get(b.userId) ?? '')))

  return (
    <div className="mx-auto w-full max-w-[1200px] px-2 pb-10 md:px-6">
      <PageHeader kicker="Team" title="Daily diary" description="Each day's commitments, time and review, written by the person who lived it. Everyone's diary is open to everyone." />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" className="grid size-9 place-items-center rounded-md border border-border bg-card hover:bg-muted" onClick={() => setMonth((m) => shiftMonth(m, -1))}><ChevronLeft className="size-4" /></button>
          <h2 className="min-w-[10rem] text-center text-base font-semibold">{new Date(`${month}-01T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h2>
          <button type="button" aria-label="Next month" className="grid size-9 place-items-center rounded-md border border-border bg-card hover:bg-muted" onClick={() => setMonth((m) => shiftMonth(m, 1))}><ChevronRight className="size-4" /></button>
        </div>
        <button type="button" className="h-9 rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-muted" onClick={() => { setMonth(todayKey().slice(0, 7)); setSelected(todayKey()) }}>Today</button>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger aria-label="Whose diary" className="h-9 w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Everyone</SelectItem>
            {(data?.people ?? []).map((p) => <SelectItem key={p.userId} value={p.userId}>{p.userId === userId ? 'Me' : p.displayName}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <section aria-label="Calendar" className="rounded-xl border border-border bg-card p-2">
          {query.isPending ? <Skeleton className="h-80 w-full" /> : (
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((d) => <div key={d} className="px-1 py-1 text-center text-tiny font-medium text-text-muted">{d}</div>)}
              {monthGrid(month).map((cell) => {
                const list = byDay.get(cell.date) ?? []
                const mine = who !== ALL ? list.find((e) => e.userId === who) : undefined
                const isSelected = cell.date === selected
                return (
                  <button
                    key={cell.date}
                    type="button"
                    onClick={() => setSelected(cell.date)}
                    aria-pressed={isSelected}
                    aria-label={`${cell.date}, ${list.length} diar${list.length === 1 ? 'y' : 'ies'}`}
                    className={cn(
                      'flex min-h-[4.75rem] flex-col rounded-lg border p-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      cell.inMonth ? 'bg-card' : 'bg-muted/40 text-text-muted',
                      isSelected ? 'border-primary ring-1 ring-primary' : 'border-border-soft hover:bg-muted',
                      cell.date === todayKey() && 'bg-brand-soft/50',
                    )}
                  >
                    <span className="flex items-center justify-between text-tiny font-semibold">
                      {cell.day}
                      {mine?.review ? <span aria-hidden>{emojiOf(mine.review.happiness)}</span> : null}
                    </span>
                    {who === ALL ? (
                      list.length > 0 ? <span className="mt-auto text-micro text-text-secondary">{list.length} diar{list.length === 1 ? 'y' : 'ies'}</span> : null
                    ) : mine ? (
                      <span className="mt-auto text-micro text-text-secondary">
                        {mine.off ? 'Out' : `${mine.outcomes.filter((o) => o.done).length}/${mine.outcomes.length} done`}
                        {!mine.off && mine.loggedMinutes > 0 ? <span className="block">{formatMinutes(mine.loggedMinutes)} logged</span> : null}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          )}
        </section>

        <section aria-label="Selected day" className="space-y-3">
          <h3 className="text-sm font-semibold">{new Date(`${selected}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
          {dayEntries.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-text-muted">No diaries on this day.</p> : null}
          {dayEntries.filter((e) => who === ALL || e.userId === who).map((e) => <EntryCard key={e.userId} entry={e} name={e.userId === userId ? 'Me' : names.get(e.userId) ?? 'Someone'} />)}
          {who !== ALL && dayEntries.length > 0 && !dayEntries.some((e) => e.userId === who) ? <p className="text-sm text-text-muted">They have no diary on this day.</p> : null}
        </section>
      </div>
    </div>
  )
}

function EntryCard({ entry, name }: { entry: DiaryMonth['entries'][number]; name: string }) {
  const follow = followThrough(entry.plan.allocatedMinutes, entry.loggedMinutes)
  return (
    <article className="rounded-xl border border-border bg-card p-4">
      <header className="mb-2 flex items-center gap-2">
        <UserAvatar person={{ id: entry.userId, displayName: name }} size="sm" />
        <span className="text-sm font-semibold">{name}</span>
        {entry.review ? <span className="ml-auto text-xl" title={HAPPINESS.find((h) => h.value === entry.review?.happiness)?.label}>{emojiOf(entry.review.happiness)}</span> : null}
      </header>
      {entry.off ? <p className="text-sm text-text-secondary">Out of the office.</p> : (
        <>
          <ul className="space-y-1.5">
            {entry.outcomes.map((o) => (
              <li key={o.id} className="flex items-start gap-2 text-sm">
                <Checkbox checked={o.done} disabled aria-label={o.done ? 'Done' : 'Not done'} className="mt-0.5" />
                <span className={cn(o.done && 'text-text-muted line-through')}>
                  {o.text}
                  {o.card ? <span className="ml-1.5 text-tiny text-text-muted">{o.card.code ?? o.card.title}</span> : o.cardId ? <span className="ml-1.5 text-tiny text-text-muted">A private item</span> : null}
                  {o.minutes > 0 ? <span className="ml-1.5 text-tiny text-text-muted">{formatMinutes(o.minutes)}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-tiny text-text-secondary">
            {formatMinutes(entry.plan.allocatedMinutes)} planned
            {entry.loggedMinutes > 0 ? ` · ${formatMinutes(entry.loggedMinutes)} logged${follow !== null ? ` (${follow}% of plan)` : ''}` : ''}
          </p>
          {entry.attention ? <Block label="Needs attention" text={entry.attention} /> : null}
          {entry.delegate.length > 0 ? <Block label="Delegated" text={entry.delegate.map((d) => `${d.text}${d.person ? ` → ${d.person}` : ''}`).join('\n')} /> : null}
        </>
      )}
      {entry.review ? (
        <div className="mt-3 border-t border-border-soft pt-2">
          {entry.review.moved ? <Block label="Moved forward" text={entry.review.moved} /> : null}
          {entry.review.stuck ? <Block label="Got stuck" text={entry.review.stuck} /> : null}
          {entry.review.tomorrow ? <Block label="Change next" text={entry.review.tomorrow} /> : null}
          {entry.review.notes ? <Block label="Notes" text={entry.review.notes} /> : null}
        </div>
      ) : null}
    </article>
  )
}

function Block({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-2">
      <p className="text-micro font-semibold uppercase tracking-wide text-text-muted">{label}</p>
      <p className="whitespace-pre-wrap text-sm">{text}</p>
    </div>
  )
}
